const players = new Set()
const activeOneShots = new Set()

let globalVolume = 1
let fadeDuration = 0.35
let audioReadyPromise = null
let mediaUnlockPromise = null

let mediaContext = null
let masterInput = null
let highpassFilter = null
let lowpassFilter = null
let masterGain = null

let highpassFrequency = 20
let lowpassFrequency = 20000
let metronomePan = -1

const isBrowser = () => typeof window !== 'undefined'
const clampVolume = (value) => Math.max(0, Math.min(1, Number(value)))
const clampFrequency = (value, min, max) => Math.max(min, Math.min(max, Number(value)))

const getAudioContextClass = () => {
    if (!isBrowser()) return null
    return window.AudioContext || window.webkitAudioContext || null
}

const hasAudioContextSupport = () => Boolean(getAudioContextClass())
const SILENT_AUDIO_SRC = "data:audio/mp4;base64,AAAAHGZ0eXBpc29tAAACAGlzb21pc28yYXA0MQAAACBtZGF0AAACrG1vb3YAAABsbXZoZAAAAAAAAAAAAAAAAAAAA+gAAAPoAAEAAAEAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAIAAAIVdHJhawAAAFx0a2hkAAAAAwAAAAAAAAAAAAAAAQAAAAAAAAPoAAAAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAQAAAAAAABQAAAAKQAAAAAAAkZWR0cwAAABxlbHN0AAAAAAAAAAEAAAPoAAAAAAABAAAAAAO5bWRpYQAAACBtZGhkAAAAAAAAAAAAAAAAAAAyAAAAMgBVxAAAAAAAKWhkbHIAAAAAAAAAAHNvdW4AAAAAAAAAAAAAAFNvdW5kSGFuZGxlcgAAAmRtaW5mAAAAFHZtaGQAAAAAAAAAAAAAAAAAAAAAZGRpbmYAAAAcZHJlZgAAAAAAAAABAAAADHVybCAAAAABAAACJHN0YmwAAABwc3RzZAAAAAAAAAABAAAAYG1wNGEAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAgACAEgAAABIAAAAAABFbmNvZGVyAAAAAAAyZXNkcwAAAAADgICATwABAASAgIBBQBQgICAQAECAgBqA4CAgAEFAgAEAgAAAACg0AAB9AAAKLAAAZCAgIAgBgCAgIAAAAAUAAAHk3d2F2ZQAAABxzdHNkAAAAAAAAAAEAAABKc3R0cwAAAAAAAAABAAAAAQAAAgAAAAAUc3RzYwAAAAAAAAABAAAAAQAAAAEAAAABAAAAFHN0c3oAAAAAAAAAAAAAAAEAAABQc3RjbwAAAAAAAAABAAAAKAAAABRidWR0YQAAAFptZXRhAAAAAAAAACFoZGxyAAAAAAAAAABtZGlyYXBwbAAAAAAAAAAAAAAAAC1pbHN0AAAAJal0b28AAAAdZGF0YQAAAAEAAAAATGF2ZjU4LjQ1LjEwMA=="

const getMediaContext = () => {
    if (!isBrowser()) return null

    if (!mediaContext) {
        const AudioContextClass = getAudioContextClass()
        if (!AudioContextClass) return null
        mediaContext = new AudioContextClass()
    }

    return mediaContext
}

const getCurrentTime = () => getMediaContext()?.currentTime ?? 0

const ensureFilterGraph = () => {
    const context = getMediaContext()
    if (!context) return null

    if (!masterInput) {
        masterInput = context.createGain()

        highpassFilter = context.createBiquadFilter()
        highpassFilter.type = 'highpass'
        highpassFilter.frequency.value = highpassFrequency

        lowpassFilter = context.createBiquadFilter()
        lowpassFilter.type = 'lowpass'
        lowpassFilter.frequency.value = lowpassFrequency

        masterGain = context.createGain()
        masterGain.gain.value = globalVolume

        masterInput.connect(highpassFilter)
        highpassFilter.connect(lowpassFilter)
        lowpassFilter.connect(masterGain)
        masterGain.connect(context.destination)
    }

    return {
        context,
        masterInput,
        highpassFilter,
        lowpassFilter,
        masterGain
    }
}

const isAudioReady = () => {
    if (!isBrowser()) return true
    if (!hasAudioContextSupport()) return true

    if (!mediaContext) {
        return false
    }

    const context = getMediaContext()
    return context?.state === 'running'
}

const ensureAudioReady = async () => {
    if (!isBrowser()) return

    if (!audioReadyPromise) {
        audioReadyPromise = (async () => {
            ensureFilterGraph()
            const context = getMediaContext()
            if (context && context.state !== 'running') {
                try {
                    await context.resume()
                } catch (error) {
                    console.warn('AudioContext resume error', error)
                }
            }
        })().finally(() => {
            audioReadyPromise = null
        })
    }

    await audioReadyPromise
}

const unlockMediaPlayback = async () => {
    if (!isBrowser()) return

    if (!mediaUnlockPromise) {
        mediaUnlockPromise = (async () => {
            const audio = new Audio(SILENT_AUDIO_SRC)
            audio.playsInline = true
            audio.muted = true
            audio.volume = 0

            try {
                await audio.play()
                audio.pause()
                audio.currentTime = 0
            } catch (error) {
                console.warn('Silent HTMLAudio unlock error', error)
            }
        })().finally(() => {
            mediaUnlockPromise = null
        })
    }

    await mediaUnlockPromise
}

const scheduleGain = (gainNode, targetValue, durationSeconds) => {
    if (!gainNode) return

    const now = getCurrentTime()
    gainNode.cancelScheduledValues(now)

    if (durationSeconds <= 0) {
        gainNode.setValueAtTime(targetValue, now)
        return
    }

    gainNode.setValueAtTime(gainNode.value, now)
    gainNode.linearRampToValueAtTime(targetValue, now + durationSeconds)
}

const createPlayer = (source) => {
    if (typeof source !== 'string') {
        throw new Error('Invalid source for createPlayer')
    }

    const audio = isBrowser() ? new Audio() : null
    if (!audio) {
        throw new Error('Audio element is not available in this environment')
    }

    audio.src = source
    audio.loop = true
    audio.preload = 'none'
    audio.playsInline = true
    audio.crossOrigin = 'anonymous'
    audio.volume = 1

    let isTargetPlaying = false
    let stopTimer = null
    let sourceNode = null
    let outputGain = null
    let preloaded = false
    let currentGain = 1

    const clearScheduledStop = () => {
        if (stopTimer) {
            clearTimeout(stopTimer)
            stopTimer = null
        }
    }

    const ensureNodes = async () => {
        await ensureAudioReady()
        const graph = ensureFilterGraph()
        if (!graph) return

        if (!sourceNode) {
            sourceNode = graph.context.createMediaElementSource(audio)
            outputGain = graph.context.createGain()
            outputGain.gain.value = 0

            sourceNode.connect(outputGain)
            outputGain.connect(graph.masterInput)
        }
    }

    const api = {
        preload: async () => {
            if (preloaded) return
            audio.preload = 'metadata'
            audio.load()
            preloaded = true
        },
        play: async () => {
            isTargetPlaying = true
            clearScheduledStop()
            await ensureNodes()

            if (!outputGain) return

            scheduleGain(outputGain.gain, 0, 0)

            try {
                await audio.play()
            } catch (error) {
                console.warn('HTMLAudio play error', error)
                return
            }

            scheduleGain(outputGain.gain, currentGain, fadeDuration)
        },
        stop: (options = {}) => {
            isTargetPlaying = false
            clearScheduledStop()
            const immediate = Boolean(options?.immediate)

            if (!outputGain) {
                audio.pause()
                audio.currentTime = 0
                return
            }

            if (immediate) {
                outputGain.gain.cancelScheduledValues(getCurrentTime())
                outputGain.gain.value = 0
                audio.pause()
                audio.currentTime = 0
                return
            }

            scheduleGain(outputGain.gain, 0, fadeDuration)
            stopTimer = setTimeout(() => {
                audio.pause()
                audio.currentTime = 0
                outputGain.gain.value = 0
                stopTimer = null
            }, fadeDuration * 1000)
        },
        setVolume: (value) => {
            currentGain = clampVolume(value)

            if (!outputGain) return
            if (!isTargetPlaying) {
                if (audio.paused) {
                    outputGain.gain.value = 0
                }
                return
            }

            scheduleGain(outputGain.gain, currentGain, 0.12)
        },
        dispose: () => {
            clearScheduledStop()
            audio.pause()
            audio.removeAttribute('src')
            audio.load()
            sourceNode?.disconnect()
            outputGain?.disconnect()
        }
    }

    players.add(api)
    return api
}

const loadAudioBuffer = async (url) => {
    if (!isBrowser()) return { url, buffer: null }

    const context = getMediaContext()
    if (!context) return { url, buffer: null }

    const response = await fetch(url)
    const arrayBuffer = await response.arrayBuffer()
    const audioBuffer = await context.decodeAudioData(arrayBuffer.slice(0))

    return {
        url,
        buffer: audioBuffer
    }
}

const stopAll = () => {
    players.forEach((player) => {
        try {
            player.stop({ immediate: true })
        } catch { }
    })
}

const stopMetronomeSounds = () => {
    activeOneShots.forEach((oneShot) => {
        try {
            oneShot.sourceNode?.stop()
        } catch { }
        try {
            oneShot.sourceNode?.disconnect()
        } catch { }
        try {
            oneShot.gainNode?.disconnect()
        } catch { }
        try {
            oneShot.pannerNode?.disconnect()
        } catch { }
    })
    activeOneShots.clear()
}

const setMasterVolume = (value) => {
    globalVolume = clampVolume(value)

    if (masterGain) {
        scheduleGain(masterGain.gain, globalVolume, 0.12)
    }

    activeOneShots.forEach((oneShot) => {
        if (oneShot?.gainNode) {
            scheduleGain(oneShot.gainNode.gain, globalVolume, 0.05)
        }
    })
}

const setHighpassFrequency = (value) => {
    highpassFrequency = clampFrequency(value, 10, 20000)

    if (highpassFilter) {
        const now = getCurrentTime()
        highpassFilter.frequency.cancelScheduledValues(now)
        highpassFilter.frequency.setValueAtTime(highpassFilter.frequency.value, now)
        highpassFilter.frequency.linearRampToValueAtTime(highpassFrequency, now + 0.12)
    }
}

const setLowpassFrequency = (value) => {
    lowpassFrequency = clampFrequency(value, 10, 22000)

    if (lowpassFilter) {
        const now = getCurrentTime()
        lowpassFilter.frequency.cancelScheduledValues(now)
        lowpassFilter.frequency.setValueAtTime(lowpassFilter.frequency.value, now)
        lowpassFilter.frequency.linearRampToValueAtTime(lowpassFrequency, now + 0.12)
    }
}

const setFadeDuration = (value) => {
    fadeDuration = Math.max(0, Number(value) / 1000)
}

const setMetronomePan = (value) => {
    metronomePan = Math.max(-1, Math.min(1, Number(value)))

    activeOneShots.forEach((oneShot) => {
        if (oneShot?.pannerNode) {
            oneShot.pannerNode.pan.value = metronomePan
        }
    })
}

const playOneShot = async (source, options = {}) => {
    if (!isBrowser() || !source) return

    await ensureAudioReady()

    const context = getMediaContext()
    if (!context) return

    const audioBuffer = source?.buffer ?? null
    if (!audioBuffer) return
    const when = Math.max(getCurrentTime(), Number(options.when ?? getCurrentTime()))

    const sourceNode = context.createBufferSource()
    sourceNode.buffer = audioBuffer

    const gainNode = context.createGain()
    gainNode.gain.value = globalVolume
    const pannerNode = context.createStereoPanner()
    pannerNode.pan.value = metronomePan

    sourceNode.connect(gainNode)
    gainNode.connect(pannerNode)
    pannerNode.connect(context.destination)

    const activeEntry = { sourceNode, gainNode, pannerNode }

    const cleanup = () => {
        activeOneShots.delete(activeEntry)
        try {
            sourceNode.disconnect()
        } catch { }
        try {
            gainNode.disconnect()
        } catch { }
        try {
            pannerNode.disconnect()
        } catch { }
    }

    sourceNode.addEventListener('ended', cleanup, { once: true })
    activeOneShots.add(activeEntry)

    try {
        sourceNode.start(when)
    } catch (error) {
        console.warn('WebAudio playOneShot error', error)
        cleanup()
    }
}

const startAudioEngine = async () => {
    await ensureAudioReady()
    await unlockMediaPlayback()
    return isAudioReady()
}

export {
    loadAudioBuffer,
    createPlayer,
    stopAll,
    stopMetronomeSounds,
    setMasterVolume,
    setHighpassFrequency,
    setLowpassFrequency,
    setFadeDuration,
    setMetronomePan,
    playOneShot,
    startAudioEngine,
    isAudioReady,
    getCurrentTime
}
